import { notify } from '@/lib/toast';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { ProductGrid } from '@/components/pos/ProductGrid';
import { CartPanel } from '@/components/pos/CartPanel';
import { CheckoutDialog } from '@/components/pos/CheckoutDialog';
import { ReceiptView } from '@/components/pos/ReceiptView';
import { LiveReceiptPreview } from '@/components/pos/LiveReceiptPreview';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { TabBar } from '@/components/ui/tab-bar';
import { X, Keyboard, HelpCircle, ShoppingCart, Package, Save, RotateCcw, MapPin, Scan, PauseCircle, List, Loader2, WifiOff, ChevronDown, UserCheck, Monitor, AlertTriangle, Clock, Receipt } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Button } from '@/components/ui/button';
import { restoredLineDiscount } from '@/lib/cart-restore';
import { useCartStore } from '@/stores/cart.store';
import { useLocationStore } from '@/stores/location.store';
import { useAuthStore } from '@/stores/auth.store';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useOfflineCache } from '@/hooks/useOfflineCache';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { usePromotionPreview } from '@/hooks/usePromotionPreview';
import { useLoyaltyRedeemPreview } from '@/hooks/useLoyaltyRedeemPreview';
import { applyPromotionToTotal } from '@/lib/pos-total';
import { useOfflineSyncPanel } from '@/stores/offline-sync-panel.store';
import { getPendingSaleCount, getOfflineCachedProducts } from '@/lib/offline-db';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { formatCurrency, formatDate } from '@/lib/utils';
import i18n from '@/lib/i18n';
import { posService } from '@/services/pos.service';
import { serialReceiptLines, type ReceiptSerialInfo } from '@/lib/receipt-serial-lines';
import type { Sale, Product, Customer } from '@/types/pos';
import type { Tenant } from '@/types/auth';
import { useScreenDocumentTitle } from '@/hooks/useScreenDocumentTitle';

const DRAFT_KEY = 'pos-draft-cart';
const DRAFT_CUSTOMER_KEY = 'pos-draft-customer';

/** Location row as returned by GET /locations (only the fields the picker reads). */
interface PosLocation {
  id: string;
  name: string;
  type?: string | null;
}

/** Held (parked) sale row as returned by GET /sales/held. */
interface HeldSale {
  id: string;
  invoiceNumber: string;
  notes?: string | null;
  createdAt: string;
  updatedAt?: string;
  customer?: Customer | null;
  discountType?: 'PERCENTAGE' | 'FIXED' | null;
  discountValue?: string | number | null;
  _count?: { items?: number };
}

/** Body of POST /sales/hold. */
interface HoldSalePayload {
  items: Array<{
    productId: string;
    variantId?: string;
    quantity: number;
    discountType?: 'PERCENTAGE' | 'FIXED';
    discountValue?: number;
    discountReason?: string;
    discountApprovedById?: string;
  }>;
  customerId?: string;
  locationId?: string;
  discountType?: 'PERCENTAGE' | 'FIXED';
  discountValue?: number;
  cartSnapshot: string;
}

/** Response of POST /auth/pos-pin-verify. */
interface PosPinStaff {
  id: string;
  name: string;
}

/** Fields of a completed sale that the ESC/POS auto-print reads beyond the shared `Sale` type. */
interface PrintableSaleItem {
  productName?: string;
  /** Present on `GET /sales/:id/receipt` lines for serial-tracked products (T-484 / T-491). */
  serialNumber?: ReceiptSerialInfo | null;
  product?: Product | null;
  quantity?: number;
  unitPrice?: number | string;
  discountAmount?: number | string;
  totalAmount?: number | string;
}

interface PrintableSale extends Omit<Sale, 'items' | 'payments'> {
  items?: PrintableSaleItem[];
  payments?: Array<{ method?: string }>;
  cashierName?: string | null;
  tipAmount?: number;
  paymentMethod?: string;
  fbrInvoiceId?: string | null;
}

/** POS-I119: line (bargaining) + order discounts of a finished sale (promotions are not on this payload). */
function youSavedOf(s: PrintableSale): number {
  const lines = Number(s.lineDiscountAmount ?? (s.items ?? []).reduce((sum, it) => sum + Number(it.discountAmount ?? 0), 0));
  return lines + Number(s.discountAmount ?? 0);
}

/** Receipt-related tenant settings carried on the auth tenant at runtime. */
interface ReceiptTenantSettings {
  address?: string;
  phone?: string;
  receiptFooter?: string;
}

export function POSPage() {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  // POS-I59: F10 opens the Payment dialog in quick-cash mode (exact cash tender, completes the sale).
  const [quickCash, setQuickCash] = useState(false);
  const [completedSaleId, setCompletedSaleId] = useState<string | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [mobileView, setMobileView] = useState<'products' | 'cart'>('products');
  const [confirmClearCart, setConfirmClearCart] = useState(false);
  const [showBarcodeInput, setShowBarcodeInput] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [showHeldOrders, setShowHeldOrders] = useState(false);
  const [showReceiptPreview, setShowReceiptPreview] = useState(() => localStorage.getItem('pos-receipt-preview') === '1');
  const toggleReceiptPreview = () => {
    setShowReceiptPreview((v) => {
      const next = !v;
      localStorage.setItem('pos-receipt-preview', next ? '1' : '0');
      return next;
    });
  };
  // Staff PIN switch
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [activeCashier, setActiveCashier] = useState<{ id: string; name: string } | null>(null);
  const { t } = useTranslation('pos');
  const { t: tNav } = useTranslation('nav');
  useScreenDocumentTitle(tNav('pos'));

  const SHORTCUTS = [
    { key: 'F2', desc: t('shortcutFocusSearch') },
    { key: 'F12', desc: t('shortcutCheckout') },
    { key: 'F3', desc: t('shortcutCheckoutAlt') },
    { key: 'F4', desc: t('shortcutBarcodeToggle') },
    { key: 'F8', desc: t('shortcutHoldOrder') },
    { key: 'F10', desc: t('shortcutQuickCheckout') },
    { key: 'Esc', desc: t('shortcutClearClose') },
    { key: '↑/↓', desc: t('shortcutSelectLine') },
    { key: '+/-', desc: t('shortcutLineQty') },
    { key: 'Del', desc: t('shortcutRemoveLine') },
    { key: '*', desc: t('shortcutLineDiscount') },
    { key: '?', desc: t('shortcutShowShortcuts') },
  ];
  const clearCart = useCartStore((s) => s.clearCart);
  const itemCount = useCartStore((s) => s.itemCount);
  const items = useCartStore((s) => s.items);
  const cartGrandTotal = useCartStore((s) => s.grandTotal);
  const cartSubtotal = useCartStore((s) => s.subtotal);
  const cartTax = useCartStore((s) => s.totalTax);
  const cartOrderDiscount = useCartStore((s) => s.orderDiscountAmount);
  const cartCustomer = useCartStore((s) => s.customer);
  const { selectedLocationName, selectedLocationId, setLocation } = useLocationStore();
  const { tenant } = useAuthStore();
  const isOnline = useOnlineStatus();
  const [offlineCount, setOfflineCount] = useState(0);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const { hasPermission } = useAuthStore();
  const [cfdWindow, setCfdWindow] = useState<Window | null>(null);

  // CFD BroadcastChannel — broadcast cart state to Customer-Facing Display
  const cfdChannel = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    cfdChannel.current = new BroadcastChannel('pos-cfd');
    return () => cfdChannel.current?.close();
  }, []);
  // POS-I58: the second screen must show the figure the till charges. The cart panel's Total is the
  // server-priced one (promotions, GST, further tax); the same hooks feed it here (same query keys,
  // so no extra requests), and the offline till prices the cart itself (POS-I78).
  const { promotionPreview: cfdPromotions, promotionDiscount: cfdPromotionDiscount } = usePromotionPreview();
  const { pricing: cfdPricing } = useLoyaltyRedeemPreview({
    customerId: cartCustomer?.id,
    redeemPoints: 0,
    locationId: selectedLocationId,
  });
  const cfdGrandTotal = cfdPricing
    ? Math.round(cfdPricing.totalAmount * 100) / 100
    : applyPromotionToTotal(cartGrandTotal, cfdPromotionDiscount);
  const cfdTotalTax = cfdPricing ? cfdPricing.totalTax : cartTax;
  const cfdPromotionKey = cfdPromotions.map((p) => `${p.promotionName}:${p.discountAmount}`).join('|');
  useEffect(() => {
    if (!cfdChannel.current) return;
    cfdChannel.current.postMessage({
      items: items.map((i) => ({
        name: i.product.name,
        variantName: i.variant?.name ?? undefined,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
        discountAmount: i.discountAmount,
        // Line value before tax, so the lines add up to the Subtotal shown under them.
        lineTotal: i.unitPrice * i.quantity - i.discountAmount,
      })),
      subtotal: cartSubtotal,
      orderDiscountAmount: cartOrderDiscount,
      promotions: cfdPromotions.map((p) => ({ name: p.promotionName, discountAmount: p.discountAmount })),
      totalTax: cfdTotalTax,
      grandTotal: cfdGrandTotal,
      customerName: cartCustomer?.name ?? undefined,
      tenantName: tenant?.name ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, cfdGrandTotal, cartSubtotal, cfdTotalTax, cartOrderDiscount, cfdPromotionKey, cartCustomer, tenant]);

  const openCfd = () => {
    if (cfdWindow && !cfdWindow.closed) {
      cfdWindow.focus();
    } else {
      const w = window.open('/cfd', 'pos-cfd', 'width=1280,height=720');
      setCfdWindow(w);
    }
  };

  // Pre-cache POS data into IndexedDB for offline use
  useOfflineCache();

  // Robust sync engine — fires on reconnect
  const { syncing, pending: pendingSync, failedCount, retryFailed } = useOfflineSync();
  const showSyncPanel = useOfflineSyncPanel((st) => st.show);

  const { data: locationsData } = useQuery({
    queryKey: ['locations-pos'],
    queryFn: () => api.get('/locations').then((r) => r.data?.data ?? r.data ?? []),
    enabled: isOnline,
  });
  const allLocations: PosLocation[] = Array.isArray(locationsData) ? locationsData : [];

  useEffect(() => {
    const update = () => getPendingSaleCount().then(setOfflineCount);
    update();
    const interval = setInterval(update, 3000);
    return () => clearInterval(interval);
  }, []);

  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const barcodeBufferRef = useRef('');
  const barcodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locationPickerRef = useRef<HTMLDivElement>(null);

  // Close location picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (locationPickerOpen && locationPickerRef.current && !locationPickerRef.current.contains(e.target as Node)) {
        setLocationPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [locationPickerOpen]);

  const queryClient = useQueryClient();

  // ─── Real-time stock updates ────────────────────────────────────────────────
  // When any stock.updated event arrives (from a purchase receive, adjustment, etc.)
  // invalidate the products query so the POS grid shows fresh quantities.
  const handleStockUpdated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pos-products'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  }, [queryClient]);
  useSocketEvent('stock.updated', handleStockUpdated);
  useSocketEvent('purchase.received', handleStockUpdated);

  // ─── Held Sales ───────────────────────────────────────────────────────────

  const { data: heldSalesData, isLoading: heldLoading } = useQuery({
    queryKey: ['held-sales'],
    queryFn: () => api.get('/sales/held', { params: { limit: 100 } }).then((r) => r.data),
    enabled: showHeldOrders,
  });
  const heldSales: HeldSale[] = Array.isArray(heldSalesData) ? heldSalesData : (heldSalesData?.data ?? []);

  const { data: heldStats } = useQuery({
    queryKey: ['held-sales-stats'],
    queryFn: () => api.get('/sales/held/stats').then((r) => r.data),
    enabled: showHeldOrders,
  });

  const holdMutation = useMutation({
    mutationFn: (payload: HoldSalePayload) => api.post('/sales/hold', payload),
    onSuccess: () => {
      toast.success('Order held — cart cleared');
      clearCart();
      queryClient.invalidateQueries({ queryKey: ['held-sales'] });
      queryClient.invalidateQueries({ queryKey: ['held-sales-stats'] });
    },
    onError: (err) => notify.error(err, 'Failed to hold order'),
  });

  const cancelHoldMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/sales/${id}/cancel-hold`),
    onSuccess: () => {
      toast.success('Held order cancelled');
      queryClient.invalidateQueries({ queryKey: ['held-sales'] });
      queryClient.invalidateQueries({ queryKey: ['held-sales-stats'] });
    },
    onError: () => toast.error('Failed to cancel held order'),
  });

  const handleHoldOrder = useCallback(() => {
    if (items.length === 0) { toast.error('Cart is empty'); return; }
    const { customer, orderDiscountType, orderDiscountValue } = useCartStore.getState();
    holdMutation.mutate({
      items: items.map((ci) => ({
        productId: ci.product.id,
        ...(ci.variant?.id ? { variantId: ci.variant.id } : {}),
        quantity: ci.quantity,
        ...(ci.discountType
          ? {
              discountType: ci.discountType,
              discountValue: ci.discountValue,
              ...(ci.discountReason ? { discountReason: ci.discountReason } : {}),
              ...(ci.discountApprovedById ? { discountApprovedById: ci.discountApprovedById } : {}),
            }
          : {}),
      })),
      ...(customer?.id ? { customerId: customer.id } : {}),
      ...(selectedLocationId ? { locationId: selectedLocationId } : {}),
      ...(orderDiscountType
        ? { discountType: orderDiscountType, discountValue: orderDiscountValue }
        : {}),
      cartSnapshot: JSON.stringify(items),
    });
  }, [items, holdMutation, selectedLocationId]);

  const handleRetrieveHeld = useCallback((sale: HeldSale) => {
    try {
      const saved = JSON.parse(sale.notes ?? '[]');
      clearCart();
      const { addItem, updateQuantity, setCustomer, setOrderDiscount, setItemDiscount } = useCartStore.getState();
      // Customer first so price-tier repricing applies to the lines added below (POS-I16).
      if (sale.customer?.id) setCustomer(sale.customer);
      for (const item of saved) {
        addItem(item.product, item.variant ?? undefined);
        if (item.quantity > 1) {
          const cartItem = useCartStore.getState().items.find(
            (ci) => ci.product.id === item.product.id && (ci.variant?.id ?? null) === (item.variant?.id ?? null),
          );
          if (cartItem) updateQuantity(cartItem.id, item.quantity);
        }
        // POS-I119: the bargaining discount on a line comes back with the held cart (it used to be dropped).
        const disc = restoredLineDiscount(item);
        if (disc) {
          const restored = useCartStore.getState().items.find(
            (ci) => ci.product.id === item.product.id && (ci.variant?.id ?? null) === (item.variant?.id ?? null),
          );
          if (restored) setItemDiscount(restored.id, disc.type, disc.value, disc.meta);
        }
      }
      if (sale.discountType && Number(sale.discountValue) > 0) {
        setOrderDiscount(sale.discountType, Number(sale.discountValue));
      }
      cancelHoldMutation.mutate(sale.id);
      setShowHeldOrders(false);
      toast.success('Cart restored from held order');
    } catch {
      toast.error('Failed to restore held order');
    }
  }, [clearCart, cancelHoldMutation]);

  // ─── Tenant settings ────────────────────────────────────────────────────────
  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 60_000,
  });

  // ─── Barcode scanner — keyboard buffer (physical scanner fires rapid keystrokes) ──

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

      // F4 toggles the visible scan input
      if (e.key === 'F4') {
        e.preventDefault();
        setShowBarcodeInput((v) => {
          if (!v) setTimeout(() => barcodeInputRef.current?.focus(), 50);
          return !v;
        });
        return;
      }

      // Physical scanner buffer — only when NOT in a text input
      if (!inInput && e.key.length === 1) {
        barcodeBufferRef.current += e.key;
        if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
        // After 100ms silence = scanner finished sending — auto-trigger without Enter
        barcodeTimerRef.current = setTimeout(() => {
          const code = barcodeBufferRef.current;
          barcodeBufferRef.current = '';
          if (code.length >= 3) triggerBarcodeSearch(code);
        }, 100);
        return;
      }
      // Scanner sends Enter after barcode — trigger immediately if buffer has content
      if (!inInput && e.key === 'Enter' && barcodeBufferRef.current.length >= 3) {
        if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
        const code = barcodeBufferRef.current;
        barcodeBufferRef.current = '';
        e.preventDefault();
        triggerBarcodeSearch(code);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // Mount-only global barcode listener; triggerBarcodeSearch is a stable useCallback
    // defined below. (T-407 exhaustive-deps: deliberate mount-only effect.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerBarcodeSearch = useCallback(async (code: string) => {
    const blockOutOfStock = tenantData?.settings?.blockOutOfStockSales !== false;

    const checkAndAddItem = (product: Product) => {
      if (blockOutOfStock && product.trackStock && product.stockQuantity <= 0) {
        toast.error(`${product.name} is out of stock`);
        return false;
      }
      const { addItem } = useCartStore.getState();
      addItem(product);
      toast.success(`Added: ${product.name}`);
      return true;
    };

    if (!isOnline) {
      // Offline: search cached products by barcode/SKU
      const cached = await getOfflineCachedProducts();
      if (cached && cached.length > 0) {
        const match = cached.find(
          (p: Product) => p.barcode === code || p.sku === code || p.name?.toLowerCase() === code.toLowerCase(),
        );
        if (match) {
          checkAndAddItem(match);
          setBarcodeInput('');
          setTimeout(() => barcodeInputRef.current?.focus(), 50);
        } else {
          toast.error(`No cached product found for: ${code}`);
        }
      } else {
        toast.error('No product cache available offline. Connect to internet to load products.');
      }
      setTimeout(() => barcodeInputRef.current?.focus(), 50);
      return;
    }

    try {
      // Try exact barcode match first (fastest path for scanner)
      const exactRes = await api.get(`/products/barcode/${encodeURIComponent(code)}`);
      const exactProduct = exactRes.data;
      if (exactProduct?.id) {
        if (checkAndAddItem(exactProduct)) {
          setBarcodeInput('');
        }
        // Refocus for next scan
        setTimeout(() => barcodeInputRef.current?.focus(), 50);
        return;
      }
    } catch {
      // 404 or error — fall through to general search
    }
    // Fallback: general search (matches name/SKU too)
    try {
      const res = await api.get(`/products?search=${encodeURIComponent(code)}&limit=5`);
      const list = res.data?.data ?? res.data ?? [];
      if (list.length === 1) {
        if (checkAndAddItem(list[0])) {
          setBarcodeInput('');
        }
      } else if (list.length === 0) {
        toast.error(`No product found for: ${code}`);
      } else {
        toast.error(`Multiple products match "${code}" — use search to select`);
      }
    } catch {
      toast.error('Barcode lookup failed');
    }
    // Always refocus for next scan
    setTimeout(() => barcodeInputRef.current?.focus(), 50);
  }, [isOnline, tenantData]);

  // Auto-add on exact barcode match without Enter — debounced 300ms after typing stops
  useEffect(() => {
    const code = barcodeInput.trim();
    if (code.length < 3) return;
    const blockOutOfStock = tenantData?.settings?.blockOutOfStockSales !== false;
    const timer = setTimeout(async () => {
      if (!isOnline) return; // offline search requires Enter press
      try {
        const exactRes = await api.get(`/products/barcode/${encodeURIComponent(code)}`);
        const exactProduct = exactRes.data;
        if (exactProduct?.id) {
          if (blockOutOfStock && exactProduct.trackStock && exactProduct.stockQuantity <= 0) {
            toast.error(`${exactProduct.name} is out of stock`);
            setBarcodeInput('');
            return;
          }
          const { addItem } = useCartStore.getState();
          addItem(exactProduct);
          toast.success(`Added: ${exactProduct.name}`);
          setBarcodeInput('');
          setTimeout(() => barcodeInputRef.current?.focus(), 50);
        }
        // No match — do nothing, user can press Enter for full search
      } catch {
        // 404 = no exact match — wait for Enter
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [barcodeInput, isOnline, tenantData]);

  const saveDraft = useCallback(() => {
    if (items.length === 0) { toast.error('Cart is empty'); return; }
    localStorage.setItem(DRAFT_KEY, JSON.stringify(items));
    const { customer } = useCartStore.getState();
    if (customer) localStorage.setItem(DRAFT_CUSTOMER_KEY, JSON.stringify(customer));
    else localStorage.removeItem(DRAFT_CUSTOMER_KEY);
    toast.success('Draft saved');
  }, [items]);

  const hasDraft = typeof window !== 'undefined' && !!localStorage.getItem(DRAFT_KEY);

  const restoreDraft = useCallback(() => {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) { toast.error('No draft saved'); return; }
    try {
      const saved = JSON.parse(raw);
      clearCart();
      const { addItem, updateQuantity, setCustomer, setItemDiscount } = useCartStore.getState();
      const rawCustomer = localStorage.getItem(DRAFT_CUSTOMER_KEY);
      if (rawCustomer) setCustomer(JSON.parse(rawCustomer) as Customer);
      for (const item of saved) {
        addItem(item.product, item.variant ?? undefined);
        if (item.quantity > 1) {
          const cartItem = useCartStore.getState().items.find(
            (ci) =>
              ci.product.id === item.product.id &&
              (ci.variant?.id ?? null) === (item.variant?.id ?? null),
          );
          if (cartItem) updateQuantity(cartItem.id, item.quantity);
        }
        // POS-I123: the line discount (type, value, reason, approver) comes back, re-priced by the store.
        const disc = restoredLineDiscount(item);
        if (disc) {
          const restored = useCartStore.getState().items.find(
            (ci) =>
              ci.product.id === item.product.id &&
              (ci.variant?.id ?? null) === (item.variant?.id ?? null),
          );
          if (restored) setItemDiscount(restored.id, disc.type, disc.value, disc.meta);
        }
      }
      localStorage.removeItem(DRAFT_KEY);
      localStorage.removeItem(DRAFT_CUSTOMER_KEY);
      toast.success('Draft restored');
    } catch {
      toast.error('Failed to restore draft');
    }
  }, [clearCart]);

  const canUseShifts = hasPermission('shifts:*') || hasPermission('shifts:manage');

  const pinMutation = useMutation({
    mutationFn: (pin: string) => api.post<PosPinStaff>('/auth/pos-pin-verify', { pin }).then(r => r.data),
    onSuccess: (staff: PosPinStaff) => {
      setActiveCashier({ id: staff.id, name: staff.name });
      setPinInput('');
      setPinError('');
      setShowPinDialog(false);
      toast.success(`Cashier switched to ${staff.name}`);
    },
    onError: () => {
      setPinError('Incorrect PIN. Please try again.');
      setPinInput('');
    },
  });

  const { data: currentShift } = useQuery({
    queryKey: ['current-shift'],
    queryFn: () => api.get('/shifts/current').then((r) => r.data),
    staleTime: 30_000,
    enabled: canUseShifts,
  });


  const handleCheckout = useCallback(() => {
    // Only enforce shift requirement when the tenant has the Shifts addon enabled
    if (canUseShifts) {
      const shiftRequired = tenantData?.settings?.requireShift !== false;
      if (shiftRequired && (!currentShift || currentShift.status !== 'OPEN')) {
        toast.error('Please open a shift before making sales');
        return;
      }
    }
    setCheckoutOpen(true);
  }, [tenantData, currentShift, canUseShifts]);

  const handleCheckoutComplete = useCallback(async (sale: Sale) => {
    setCompletedSaleId(sale.id);
    setCheckoutOpen(false);
    setMobileView('products');

    // Broadcast checkout complete to CFD
    cfdChannel.current?.postMessage({ items: [], subtotal: 0, orderDiscountAmount: 0, totalTax: 0, grandTotal: 0, checkoutComplete: true });

    // Auto-print receipt via ESC/POS printer if one is connected
    try {
      const { printerService } = await import('@/lib/printer');
      if (printerService.isConnected()) {
        const { tenant } = useAuthStore.getState();
        // Thermal prints from the SAME `getReceipt` item data as the screen / print / reprint receipt
        // (the createSale response carries no serial block). Fall back to the committed sale if the
        // read fails (offline) so a printer never blocks on it (T-491).
        let s: PrintableSale = sale;
        try {
          const receipt = await posService.getReceipt(sale.id);
          // Only the items come from the receipt read - every other field stays as before.
          // POS-I114: the FBR number comes from the receipt read, which hides placeholders / non-live shops.
          s = { ...sale, items: receipt.items, fbrInvoiceId: receipt.fbrInvoiceId ?? null } as unknown as PrintableSale;
        } catch {
          s = { ...sale, fbrInvoiceId: null } as PrintableSale;
        }
        const enReceipt = i18n.getFixedT('en', 'receipt');
        const serialLabels = {
          serialNo: enReceipt('serialNo'),
          imei: enReceipt('imei'),
          warrantyUntil: enReceipt('warrantyUntil'),
        };
        const tenantSettings: ReceiptTenantSettings =
          (tenant as (Tenant & { settings?: ReceiptTenantSettings }) | null)?.settings ?? {};
        await printerService.printReceipt({
          storeName: tenant?.name ?? 'POS_Light',
          storeAddress: tenantSettings.address as string | undefined,
          storePhone: tenantSettings.phone as string | undefined,
          invoiceNo: s.invoiceNumber ?? s.id.slice(-8).toUpperCase(),
          date: new Date(s.createdAt ?? Date.now()).toLocaleString(),
          cashier: s.cashierName ?? undefined,
          items: (s.items ?? []).map((it: PrintableSaleItem) => ({
            name: it.productName ?? it.product?.name ?? 'Item',
            qty: it.quantity ?? 0,
            price: formatCurrency(Number(it.unitPrice ?? 0)),
            total: formatCurrency(Number(it.totalAmount ?? (Number(it.unitPrice ?? 0) * (it.quantity ?? 0)))),
            // POS-I119: the line (bargaining) discount prints under its item.
            discount: Number(it.discountAmount ?? 0) > 0 ? formatCurrency(Number(it.discountAmount)) : undefined,
            detailLines: serialReceiptLines(it.serialNumber, serialLabels, formatDate).map(
              (l) => `${l.label}: ${l.value}`,
            ),
          })),
          subtotal: formatCurrency(Number(s.subtotal ?? s.totalAmount ?? 0)),
          discount: s.discountAmount > 0 ? `-${formatCurrency(Number(s.discountAmount))}` : undefined,
          youSaved: youSavedOf(s) > 0 ? formatCurrency(youSavedOf(s)) : undefined,
          tax: s.taxAmount > 0 ? formatCurrency(Number(s.taxAmount)) : undefined,
          tip: (s.tipAmount ?? 0) > 0 ? formatCurrency(Number(s.tipAmount)) : undefined,
          total: formatCurrency(Number(s.totalAmount ?? 0)),
          paymentMethod: (s.payments?.[0]?.method ?? s.paymentMethod ?? 'CASH').replace(/_/g, ' '),
          fbrInvoiceId: s.fbrInvoiceId ?? undefined,
          footer: (tenantSettings.receiptFooter as string | undefined) ?? 'Thank you for shopping!',
        });
      }
    } catch {
      // printer errors are non-blocking — receipt will still appear on screen
    }
  }, []);

  useEffect(() => {
    if (!checkoutOpen) setQuickCash(false);
  }, [checkoutOpen]);

  const handleCheckoutClose = useCallback(() => {
    setCheckoutOpen(false);
  }, []);

  const handleReceiptClose = useCallback(() => {
    setCompletedSaleId(null);
  }, []);

  const handleClose = () => {
    if (window.opener || window.history.length <= 1) {
      window.close();
    } else {
      window.location.href = '/dashboard';
    }
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

      if (e.key === 'F2') {
        e.preventDefault();
        const searchInput = document.querySelector<HTMLInputElement>('[data-pos-search]');
        searchInput?.focus();
        return;
      }
      if (e.key === 'F4') {
        // handled in barcode effect
        return;
      }
      if (e.key === 'F12') {
        e.preventDefault();
        if (itemCount > 0 && !checkoutOpen) { setQuickCash(false); handleCheckout(); }
        return;
      }
      if (e.key === 'F10') {
        e.preventDefault();
        if (itemCount > 0 && !checkoutOpen) { setQuickCash(true); handleCheckout(); }
        return;
      }
      if (e.key === 'F8') {
        e.preventDefault();
        if (itemCount > 0 && !checkoutOpen) handleHoldOrder();
        return;
      }
      if (e.key === 'F3') {
        e.preventDefault();
        if (itemCount > 0 && !checkoutOpen) { setQuickCash(false); handleCheckout(); }
        return;
      }
      if (e.key === 'Escape') {
        if (checkoutOpen) { setCheckoutOpen(false); return; }
        if (showShortcuts) { setShowShortcuts(false); return; }
        if (showHeldOrders) { setShowHeldOrders(false); return; }
        if (itemCount > 0 && !isInput) { setConfirmClearCart(true); return; }
        return;
      }
      if (e.key === '?' && !isInput) {
        setShowShortcuts((v) => !v);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [checkoutOpen, showShortcuts, showHeldOrders, itemCount, clearCart, handleCheckout, handleHoldOrder]);

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      {/* ── POS Top Header Bar (desktop only) ── */}
      <div className="hidden sm:flex min-h-[56px] shrink-0 flex-wrap items-center gap-2 gap-y-1.5 border-b bg-card/90 backdrop-blur-sm px-3 py-1.5 relative z-30">
        {/* Left: Close + Shortcuts */}
        <button
          onClick={handleClose}
          className="flex items-center gap-1.5 rounded-md border bg-background min-h-[44px] px-3 text-sm font-medium text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
          title={t('topClosePos')}
        >
          <X className="h-4 w-4" />
          {t('close', { ns: 'common' })}
        </button>

        <button
          onClick={() => setShowShortcuts((v) => !v)}
          className="flex items-center gap-1.5 rounded-md border bg-background min-h-[44px] px-3 text-sm font-medium text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
          title={t('topKeyboardShortcutsTip')}
        >
          <Keyboard className="h-4 w-4" />
          {t('shortcuts')}
        </button>

        {/* Centre: Location picker */}
        <div className="relative" ref={locationPickerRef}>
          <button
            onClick={() => setLocationPickerOpen((v) => !v)}
            className="flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 min-h-[44px] px-3 text-sm font-medium text-primary-text hover:bg-primary/10 transition-colors"
          >
            <MapPin className="h-4 w-4" />
            {selectedLocationName ?? t('allLocations')}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </button>
          {locationPickerOpen && (
            <div className="absolute start-0 top-full mt-1 z-50 min-w-[180px] rounded-md border bg-popover shadow-md py-1">
              <button
                className={`w-full text-start min-h-[44px] px-3 text-sm hover:bg-accent transition-colors ${!selectedLocationId ? 'font-semibold text-primary-text' : 'text-foreground'}`}
                onClick={() => { setLocation(null, null); setLocationPickerOpen(false); }}
              >
                {t('allLocations')}
              </button>
              {allLocations.map((loc) => (
                <button
                  key={loc.id}
                  className={`w-full text-start min-h-[44px] px-3 text-sm hover:bg-accent transition-colors ${selectedLocationId === loc.id ? 'font-semibold text-primary-text' : 'text-foreground'}`}
                  onClick={() => { setLocation(loc.id, loc.name); setLocationPickerOpen(false); }}
                >
                  {loc.name}
                  {loc.type && <span className="ms-1 opacity-50">({loc.type})</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Offline status badge */}
        {!isOnline && (
          <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive-text animate-pulse">
            <WifiOff className="h-3 w-3" />
            {t('offline')}
          </span>
        )}

        {/* Right: Draft save/restore + Barcode + Hold */}
        <div className="ms-auto flex items-center gap-1.5">
          {/* Barcode scanner toggle */}
          <button
            onClick={() => { setShowBarcodeInput((v) => !v); setTimeout(() => barcodeInputRef.current?.focus(), 50); }}
            className={`flex items-center gap-1.5 rounded-md border min-h-[44px] px-3 text-sm font-medium shadow-sm transition-colors ${showBarcodeInput ? 'bg-primary/10 border-primary/30 text-primary-text' : 'bg-background text-muted-foreground hover:bg-accent hover:text-foreground'}`}
            title={t('topToggleScannerTip')}
          >
            <Scan className="h-4 w-4" />
            {t('scan')}
          </button>

          {/* Hold Order */}
          {itemCount > 0 && (
            <button
              onClick={handleHoldOrder}
              disabled={holdMutation.isPending}
              className="flex items-center gap-1.5 rounded-md border bg-warning/10 border-warning/30 min-h-[44px] px-3 text-sm font-medium text-warning-text shadow-sm hover:bg-warning/10 transition-colors disabled:opacity-60"
              title={t('topHoldOrderTip')}
            >
              <PauseCircle className="h-4 w-4" />
              {t('holdOrder')}
            </button>
          )}

          {/* Held Orders */}
          <button
            onClick={() => setShowHeldOrders(true)}
            className="relative flex items-center gap-1.5 rounded-md border bg-background min-h-[44px] px-3 text-sm font-medium text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
            title={t('topRetrieveHeldTip')}
          >
            <List className="h-4 w-4" />
            {t('held')}
          </button>

          {/* Switch Cashier (Staff PIN) */}
          <button
            onClick={() => { setPinInput(''); setPinError(''); setShowPinDialog(true); }}
            className="flex items-center gap-1.5 rounded-md border bg-background min-h-[44px] px-3 text-sm font-medium text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
            title={t('topSwitchCashierTip')}
          >
            <UserCheck className="h-4 w-4" />
            {activeCashier ? activeCashier.name : t('topSwitchCashier')}
          </button>

          {/* Customer-Facing Display */}
          <button
            onClick={openCfd}
            className="flex items-center gap-1.5 rounded-md border bg-background min-h-[44px] px-3 text-sm font-medium text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
            title={t('topOpenCfdTip')}
          >
            <Monitor className="h-4 w-4" />
            {t('topCfd')}
          </button>

          {/* Live receipt preview toggle */}
          <button
            onClick={toggleReceiptPreview}
            className={`hidden lg:flex items-center gap-1.5 rounded-md border min-h-[44px] px-3 text-sm font-medium shadow-sm transition-colors ${showReceiptPreview ? 'bg-primary/10 border-primary/30 text-primary-text' : 'bg-background text-muted-foreground hover:bg-accent hover:text-foreground'}`}
            title={showReceiptPreview ? t('hideReceiptPreview') : t('showReceiptPreview')}
          >
            <Receipt className="h-4 w-4" />
            {t('receiptPreview')}
          </button>

          {hasDraft && (
            <button
              onClick={restoreDraft}
              className="flex items-center gap-1.5 rounded-md border bg-warning/10 border-warning/30 min-h-[44px] px-3 text-sm font-medium text-warning-text shadow-sm hover:bg-warning/10 transition-colors"
              title={t('topRestoreDraftTip')}
            >
              <RotateCcw className="h-4 w-4" />
              {t('restoreDraft')}
            </button>
          )}
          {itemCount > 0 && (
            <button
              onClick={saveDraft}
              className="flex items-center gap-1.5 rounded-md border bg-background min-h-[44px] px-3 text-sm font-medium text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground transition-colors"
              title={t('topSaveDraftTip')}
            >
              <Save className="h-4 w-4" />
              {t('saveDraft')}
            </button>
          )}
        </div>
      </div>

      {/* Offline banner — subtle full-width strip, not blocking content */}
      {!isOnline && (
        <div className="shrink-0 flex items-center justify-center gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-1 text-xs font-medium text-destructive-text">
          <WifiOff className="h-3.5 w-3.5 shrink-0" />
          <span>
            {t('offlineQueuedMsg')}
            {offlineCount > 0 && <span className="ms-1 font-semibold">({offlineCount} pending)</span>}
          </span>
        </div>
      )}

      {/* Sync status strip — shown when reconnected and sales are syncing, waiting or were refused (POS-I65) */}
      {isOnline && (syncing || pendingSync > 0 || failedCount > 0) && (
        <div className={`shrink-0 flex items-center justify-between gap-2 border-b px-4 py-1 text-xs font-medium ${
          failedCount > 0 && pendingSync === 0 && !syncing
            ? 'border-destructive/30 bg-destructive/10 text-destructive-text'
            : 'border-warning/30 bg-warning/10 text-warning-text'
        }`}>
          <div className="flex items-center gap-2">
            {syncing || pendingSync > 0 ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            ) : (
              <WifiOff className="h-3.5 w-3.5 shrink-0" />
            )}
            <span>
              {syncing
                ? `Syncing ${pendingSync} offline sale${pendingSync !== 1 ? 's' : ''}…`
                : pendingSync > 0
                  ? `${pendingSync} offline sale${pendingSync !== 1 ? 's' : ''} pending sync`
                  : `${failedCount} sale${failedCount !== 1 ? 's' : ''} failed to sync`}
              {(syncing || pendingSync > 0) && failedCount > 0 && ` · ${failedCount} failed`}
            </span>
          </div>
          {failedCount > 0 && (
            <div className="flex items-center gap-3">
              <button
                onClick={showSyncPanel}
                className="underline text-xs font-semibold hover:opacity-80"
              >
                Review
              </button>
              <button
                onClick={retryFailed}
                className="underline text-xs font-semibold hover:opacity-80"
              >
                Retry all
              </button>
            </div>
          )}
        </div>
      )}

      {/* Barcode input bar */}
      {showBarcodeInput && (
        <div className="hidden sm:flex h-12 shrink-0 items-center gap-2 border-b bg-primary/10 px-3">
          <Scan className="h-4 w-4 text-primary-text shrink-0" />
          <input
            ref={barcodeInputRef}
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const code = barcodeInput.trim();
                if (code.length >= 3) triggerBarcodeSearch(code);
                else toast.error('Barcode too short');
              }
            }}
            autoFocus
            placeholder={t('barcodeInputPlaceholder')}
            className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
          <button
            onClick={() => setShowBarcodeInput(false)}
            aria-label={t('closeBarcodeBar')}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="sm:hidden shrink-0">
        <TabBar
          bordered={false}
          equal
          tabs={[
            { key: 'products', label: t('products'), icon: Package },
            { key: 'cart', label: t('cart'), icon: ShoppingCart, count: itemCount > 0 ? itemCount : null },
          ]}
          activeTab={mobileView}
          onTabChange={(key) => setMobileView(key as 'products' | 'cart')}
        />
      </div>

      {/* Main layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Product grid — left side */}
        <div className={`flex-1 overflow-hidden ${mobileView === 'cart' ? 'hidden sm:flex' : 'flex'} flex-col`}>
          <ProductGrid />
        </div>

        {/* Cart panel — right side */}
        <div className={`sm:w-96 w-full overflow-hidden ${mobileView === 'products' ? 'hidden sm:flex' : 'flex'} flex-col`}>
          <CartPanel onCheckout={handleCheckout} />
        </div>

        {/* Live receipt preview — third rail on large screens */}
        {showReceiptPreview && (
          <div className="hidden lg:flex w-80 shrink-0 overflow-hidden flex-col">
            <LiveReceiptPreview onClose={toggleReceiptPreview} />
          </div>
        )}
      </div>

      {/* Floating cart button — mobile only, visible on products view */}
      {mobileView === 'products' && itemCount > 0 && (
        <button
          className="lg:hidden fixed bottom-4 end-4 z-40 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-primary-foreground shadow-lg shadow-primary/40 text-sm font-medium"
          onClick={() => setMobileView('cart')}
        >
          <ShoppingCart className="h-4 w-4" />
          <span>{t('cart')}</span>
          <span className="ms-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-background text-primary-text text-xs font-bold">{itemCount}</span>
        </button>
      )}

      {/* Shortcuts cheat sheet */}
      {showShortcuts && (
        <div className="absolute top-12 start-3 z-50 rounded-xl border bg-card/95 backdrop-blur-sm shadow-xl p-4 min-w-[200px]">
          <div className="flex items-center gap-1.5 mb-3">
            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-xs font-semibold text-foreground">{t('keyboardShortcuts')}</p>
          </div>
          <div className="space-y-2">
            {SHORTCUTS.map((s) => (
              <div key={s.key} className="flex items-center justify-between gap-6">
                <span className="text-xs text-muted-foreground">{s.desc}</span>
                <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono font-semibold">{s.key}</kbd>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Held Orders Dialog */}
      <Dialog
        visible={showHeldOrders}
        onHide={() => setShowHeldOrders(false)}
        header={
          <div className="flex items-center gap-2">
            <List className="h-4 w-4 text-warning-text" />
            <span>{t('heldOrders')}</span>          </div>
        }
        style={{ width: '40rem', maxWidth: '95vw' }}
        draggable={false}
        resizable={false}
      >
        {heldLoading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('loading', { ns: 'common' })}
          </div>
        ) : heldSales.length === 0 ? (
          <div className="py-10 text-center">
            <PauseCircle className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{t('noHeldOrders')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {heldStats && heldStats.count > 0 && (
              <div className="rounded-lg border bg-warning/10 border-warning/30 p-3 mb-1">
                <div className="flex items-center gap-4 flex-wrap text-xs">
                  <span className="flex items-center gap-1.5 font-medium text-warning-text">
                    <PauseCircle className="h-4 w-4" />
                    {t('heldOrderCount', { count: heldStats.count })}
                  </span>
                  <span className="text-warning-text">
                    {formatCurrency(Number(heldStats.totalValue ?? 0))} {t('onHoldValue')}
                  </span>
                  <span className="flex items-center gap-1.5 text-warning-text">
                    <Clock className="h-3.5 w-3.5" />
                    {t('oldestAge', { hours: heldStats.oldestAgeHours })}
                  </span>
                  {heldStats.expiringSoon > 0 && (
                    <span className="flex items-center gap-1.5 font-medium text-destructive-text">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {t('expiringSoon', { count: heldStats.expiringSoon })}
                    </span>
                  )}
                </div>
              </div>
            )}
            {heldSales.map((sale: HeldSale) => {
              const ageHours = Math.floor((Date.now() - new Date(sale.updatedAt ?? sale.createdAt).getTime()) / 3600000);
              const isExpiringSoon = heldStats?.expiryHours
                ? ageHours >= heldStats.expiryHours * 0.75
                : false;
              return (
              <div key={sale.id} className="flex items-center justify-between rounded-lg border p-3 bg-muted/20 hover:bg-muted/40 transition-colors">
                <div>
                  <p className="font-mono text-sm font-semibold flex items-center gap-2">
                    {sale.invoiceNumber}
                    {isExpiringSoon && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive-text px-2 py-0.5 text-xs font-medium">
                        <AlertTriangle className="h-3 w-3" />
                        {t('expiringSoonBadge')}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {sale.customer?.name ?? t('walkIn')} · {sale._count?.items ?? 0} item{(sale._count?.items ?? 0) !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-muted-foreground">{formatDate(sale.createdAt)}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-11 text-sm"
                    onClick={() => handleRetrieveHeld(sale)}
                  >
                    {t('retrieve')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-11 text-sm text-destructive-text border-destructive/30 hover:bg-destructive/10"
                    onClick={() => cancelHoldMutation.mutate(sale.id)}
                    disabled={cancelHoldMutation.isPending}
                  >
                    {t('cancel', { ns: 'common' })}
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </Dialog>

      {/* Clear-cart confirmation */}
      <ConfirmDialog
        open={confirmClearCart}
        onCancel={() => setConfirmClearCart(false)}
        onConfirm={() => { clearCart(); setConfirmClearCart(false); }}
        title={t('clearCart')}
        message={`${t('clear', { ns: 'common' })} ${items.length} item${items.length !== 1 ? 's' : ''} from cart?`}
        confirmLabel={t('clearCart')}
        variant="danger"
      />

      {/* Checkout dialog */}
      <CheckoutDialog
        open={checkoutOpen}
        quickCash={quickCash}
        onClose={handleCheckoutClose}
        onComplete={handleCheckoutComplete}
      />

      {/* Receipt viewer */}
      <ReceiptView
        saleId={completedSaleId}
        onClose={handleReceiptClose}
      />

      {/* Staff PIN Switch Dialog */}
      <Dialog
        visible={showPinDialog}
        onHide={() => { setShowPinDialog(false); setPinInput(''); setPinError(''); }}
        header={t('topSwitchCashier')}
        modal
        style={{ width: '320px' }}
        breakpoints={{ '640px': '95vw' }}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Enter your 4-digit PIN to switch the active cashier.</p>
          <div className="flex flex-col items-center gap-3">
            {/* PIN dots display */}
            <div className="flex gap-3">
              {[0,1,2,3].map((i) => (
                <div key={i} className={`h-4 w-4 rounded-full border-2 transition-all ${i < pinInput.length ? 'bg-primary border-primary' : 'bg-muted border-border'}`} />
              ))}
            </div>
            {/* PIN numpad */}
            <div className="grid grid-cols-3 gap-2 w-full max-w-[240px]">
              {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((key) => (
                <button
                  key={key || 'empty'}
                  disabled={!key || pinMutation.isPending}
                  onClick={() => {
                    if (!key) return;
                    if (key === '⌫') {
                      setPinInput(p => p.slice(0, -1));
                      setPinError('');
                    } else if (pinInput.length < 4) {
                      const next = pinInput + key;
                      setPinInput(next);
                      setPinError('');
                      if (next.length === 4) pinMutation.mutate(next);
                    }
                  }}
                  className={`h-12 rounded-lg text-lg font-semibold border transition-colors ${!key ? 'invisible' : 'bg-muted hover:bg-muted/80 active:scale-95 disabled:opacity-60'}`}
                >
                  {key}
                </button>
              ))}
            </div>
            {pinError && <p className="text-sm text-destructive-text font-medium">{pinError}</p>}
            {pinMutation.isPending && <p className="text-sm text-muted-foreground">Verifying…</p>}
          </div>
          {activeCashier && (
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-center">
              <p className="text-xs text-muted-foreground">Current cashier</p>
              <p className="font-semibold text-sm">{activeCashier.name}</p>
              <button
                className="mt-1 text-sm text-destructive-text hover:underline"
                onClick={() => { setActiveCashier(null); setShowPinDialog(false); toast.success('Cashier cleared'); }}
              >
                Clear cashier
              </button>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}
